import api from "../../../utils/api";
import {
  WORK_HOURS_ENDPOINTS,
  type EstimateMethod,
  type ExpectedProjectLine,
  type ExpectedRule,
  type ExpectedToDate,
  type HourBucket,
  type HoursSource,
  type PricedHoursLedger,
  type PullRequestRef,
  type RecordedHoursSummary,
  type RepoCost,
  type OutOfAllocationLine,
  type TimeEntryApproval,
  type TimeEntryApprovalStatus,
  type TimeEntryEvidence,
  type TimeEntryFlag,
  type TimeEntryKind,
  type TimeEntryMeeting,
  type TimeEntrySource,
  type WorkHoursIdentity,
  type WorkHoursSummary,
  type ActivityType,
  type WorkActivity,
} from "@torre/work-hours";

// ---------------------------------------------------------------------------
// Cliente API de la app Horas Trabajadas (#537). Las respuestas vienen como
// `{ meta, data }` (`response` del backend): se devuelve `data`.
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const data = <T>(res: any): T => res?.data?.data as T;

// ---------------------------------------------------------------------------
// Tiempos de espera por llamada. Viven aquí —junto a la llamada que los
// necesita— y no repartidos por los componentes: quien cambia el endpoint ve al
// lado cuánto se le concede.
//
// El tope por defecto del cliente (30 s) sirve para leer. Para las llamadas que
// ESCRIBEN es una trampa: axios corta, el servidor sigue trabajando y termina,
// y la pantalla da por fallido un trabajo ya hecho. Con un reporte eso es caro
// de verdad —la persona vuelve a pulsar y el cliente recibe el correo dos
// veces—, así que estas dos llamadas llevan su propio tope, holgado.
// ---------------------------------------------------------------------------

/**
 * Disparo manual de un reporte: 5 minutos.
 *
 * Arma el resumen del día o de la semana, lo renderiza y lo ENVÍA por correo
 * (y WhatsApp) a administradores o a cada cliente activo. Con varios clientes
 * y proveedores lentos se pasa de 30 s sin despeinarse. Si aun así se agota,
 * quien llama no puede negar el envío: hay que releer la bitácora de entregas
 * antes de concluir nada (ver `describeRequestFailure`).
 */
export const REPORT_TIMEOUT_MS = 300_000;

/**
 * Guardar una identidad: 2 minutos.
 *
 * Al guardar, el backend re-liga los commits históricos que ahora corresponden
 * a esa persona; con un histórico grande pasa de largo los 30 s. Si se corta,
 * la identidad puede haber quedado guardada igual.
 */
export const IDENTITY_TIMEOUT_MS = 120_000;

export interface WorkHoursMe {
  userId: string;
  userName: string | null;
  email: string | null;
  companyId: string;
  isAdmin: boolean;
  identity: (WorkHoursIdentity & { id?: string }) | null;
}

export interface SummaryResponse {
  /**
   * Desde #783 el backend manda el resumen con la regla de conteo (minutos de
   * entradas; días sin entradas estimados por commits). Los campos nuevos son
   * opcionales para tolerar un backend anterior.
   */
  summary: WorkHoursSummary &
    Partial<RecordedHoursSummary> & {
      /** #969: esperado a la fecha vs real (equipo, por persona y proyecto). */
      expected?: ExpectedToDate;
    };
  /** `time_entries`, `commits`, `mixed` o `none` (#783). */
  hoursSource?: RecordedHoursSummary["hoursSource"];
  /** Hay al menos un día-persona estimado en el rango (#783). */
  estimated?: boolean;
  /** #960: cómo se estiman los días sin entradas (bloques de tiempo real). */
  estimateMethod?: EstimateMethod;
  /** Repos con horas EN EL RANGO consultado (ya no es el catálogo histórico). */
  repos: string[];
  scope: "user" | "team";
  userId: string | null;
  users?: { userId: string; userName: string; githubLogin: string | null }[];
  /**
   * Squads para el filtro del tablero (solo admin). `hasNoSquad` = hay gente
   * fuera de todo squad, así que «Sin squad» es una opción que sí filtra algo.
   * Opcional: un backend anterior no lo manda y el filtro no se pinta.
   */
  squads?: { list: { id: string; name: string }[]; hasNoSquad: boolean };
  /** Squad con el que se calculó la respuesta (`none` = sin squad; `null` = todos). */
  squadId?: string | null;
  /**
   * #1063: costo de las horas de cada repo, por moneda. Lleva sueldos: SOLO lo
   * manda el servidor a los dueños; a un dev no le llega el campo.
   */
  repoCost?: RepoCost;
  /** Rango efectivo que respondió el backend (puede no ser el pedido). */
  from?: string;
  to?: string;
  /** El rango pedido superaba el máximo y se recortó por el extremo antiguo. */
  rangeClamped?: boolean;
  /** Se alcanzó el tope de franjas leídas: el resumen puede quedarse corto. */
  truncated?: boolean;
  /** No se pudieron resolver todos los títulos/enlaces de PR. */
  pullRequestInfoTruncated?: boolean;
}

export interface EntryCommit {
  sha: string;
  repos: string[];
  branches: string[];
  message: string | null;
  url: string | null;
  authoredAt: string;
  pullRequests: PullRequestRef[];
}

export type HourEntry = HourBucket & {
  commits: EntryCommit[];
  /** #783: la franja estima su día (sin entradas) o es solo evidencia. */
  estimated?: boolean;
  source?: HoursSource;
  /** Minutos registrados en entradas ese día-persona. */
  dayMinutes?: number;
  /** #960: en un día estimado, minutos del DÍA por bloques y sus bloques. */
  estimateMethod?: EstimateMethod;
  dayEstimatedMinutes?: number;
  dayBlocks?: { start: string; end: string; minutes: number }[];
};

export interface UnmatchedAuthor {
  authorLogin: string | null;
  authorEmail: string | null;
  authorName: string | null;
  commits: number;
  repos: string[];
  lastAt: string | null;
}

/**
 * Cliente = Empresa del CRM en etapa Cliente (#973). Nombre y etapa son del
 * CRM; aquí solo se configuran `emails`, `repos` y `active` (reporte semanal).
 */
export interface WorkHoursClientRow {
  id: string;
  name: string;
  emails: string[];
  repos: string[];
  active?: boolean;
  /** Etapa del CRM (siempre `customer` en la lista). */
  stage?: string | null;
  /** Esta empresa ya le puso repos/correos. */
  configured?: boolean;
  /** Otra empresa registra las horas de este cliente: no se edita aquí. */
  lockedByOtherCompany?: boolean;
  /** Vista del cliente (#978): ¿muestra los nombres del equipo? Por omisión no. */
  shareTeam?: boolean;
}

/**
 * Token de cliente (#977): SOLO lectura de `/tenantView/*` para ESE cliente.
 * Lo guarda el backend de la app del cliente (WAYAK, Dumax…) como secreto
 * `CC_CONSOLE_TENANT_TOKEN`; nunca va al navegador del cliente.
 */
export interface ClientToken {
  token: string;
  tokenId: string;
  clientId: string;
  companyId: string;
  expirationDate: string;
}

export async function issueClientToken(clientId: string, name: string): Promise<ClientToken> {
  // `POST /session/apiToken` responde `{ message, data: { token… } }` dentro de `data`.
  const body = data<{ message: string; data: ClientToken }>(
    await api.post("/session/apiToken", { clientId, name, scopes: ["read"], expiresInDays: 365 }),
  );
  return body.data;
}

/** Lo único que Horas Trabajadas puede cambiar de un cliente del CRM. */
export type WorkHoursClientConfig = Pick<WorkHoursClientRow, "emails" | "repos" | "active" | "shareTeam">;

export interface DeliveryRow {
  id: string;
  type: "daily_admin" | "weekly_client";
  channel: "email" | "whatsapp";
  status: "sent" | "preview" | "not_configured" | "failed";
  recipients: string[];
  subject: string;
  text: string;
  html: string;
  shortText: string;
  error: string | null;
  meta: Record<string, unknown>;
  createdAt: string;
}

export interface RunRow {
  id: string;
  status: "running" | "succeeded" | "failed";
  date: string;
  startedAt: string;
  finishedAt: string | null;
  repos: number;
  commits: number;
  errors: string[];
  host: string | null;
}

export interface AllocationResponse {
  userId: string;
  linked: boolean;
  from?: string;
  to?: string;
  teamMember: { id: string; name: string; role: string | null; weeklyCapacityHours: number } | null;
  allocations: { projectId: string; projectName: string; percentage: number; repos?: string[] }[];
  allocatedPercentage?: number;
  plannedVsActual: {
    projects: {
      projectId: string;
      projectName: string;
      percentage: number;
      repos: string[];
      plannedHours: number;
      actualHours: number;
      /** #783: parte de minutos registrados y parte estimada por commits. */
      recordedHours?: number;
      estimatedHours?: number;
      ratio: number | null;
    }[];
    unassignedHours: number;
    /** #843: línea «Fuera de asignación» (horas que cuentan, fuera de la asignación, con motivo). */
    outOfAllocation?: OutOfAllocationLine;
    totalHours: number;
    capacityHours: number;
    recordedHours?: number;
    estimatedHours?: number;
    estimatedDays?: string[];
  } | null;
  /** #969: esperado a la fecha (cortado a hoy) vs real de esta persona, por proyecto. */
  expected?: {
    expectedHours: number;
    actualHours: number;
    gapHours: number;
    hoursPerDay: number;
    byProject: ExpectedProjectLine[];
    /** `false` = no entra al esperado (contratista, inactivo, sin GitHub o sin asignaciones). */
    counted: boolean;
    nonTracked: boolean;
    rule: ExpectedRule;
  };
}

export interface SummaryParams {
  from?: string;
  to?: string;
  userId?: string;
  repo?: string;
  /** Solo admin (#978): acota el tablero a un cliente (sus repos y proyectos). */
  clientId?: string;
  /**
   * Solo dueños/admin: acota TODO el tablero a la gente de un squad. `none` =
   * quien no está en ningún squad.
   */
  squadId?: string;
}

export async function fetchMe(): Promise<WorkHoursMe> {
  return data(await api.get(WORK_HOURS_ENDPOINTS.me));
}

export async function fetchSummary(params: SummaryParams): Promise<SummaryResponse> {
  return data(await api.get(WORK_HOURS_ENDPOINTS.summary, { params }));
}

/** Respuesta de `GET /workHoursDetail` (#1344): el libro del rango, con costos solo para dueños. */
export interface DetailResponse {
  detail: PricedHoursLedger;
  from?: string;
  to?: string;
  scope?: "user" | "team";
  userId?: string | null;
  rangeClamped?: boolean;
  truncated?: boolean;
}

/** Las líneas detrás de cada cifra del tablero. Mismos filtros que el resumen. */
export async function fetchDetail(params: SummaryParams): Promise<DetailResponse> {
  return data(await api.get(WORK_HOURS_ENDPOINTS.detail, { params }));
}

/**
 * Horas por repo de una ventana de comparación (#1062). Es el mismo resumen
 * (misma regla y mismo alcance por rol: a un dev el servidor le fuerza su
 * propio `userId`); sólo se conserva lo que el comparativo usa.
 */
export async function fetchRepoHours(params: SummaryParams): Promise<{ byRepo: { repo: string; hours: number }[]; rangeClamped: boolean }> {
  const res = await fetchSummary(params);
  return { byRepo: res?.summary?.byRepo ?? [], rangeClamped: Boolean(res?.rangeClamped) };
}

export async function fetchEntries(
  params: SummaryParams & { date?: string; weekday?: number; hour?: number },
): Promise<HourEntry[]> {
  return data(await api.get(WORK_HOURS_ENDPOINTS.entries, { params }));
}

export async function fetchAllocations(params: SummaryParams): Promise<AllocationResponse> {
  return data(await api.get(WORK_HOURS_ENDPOINTS.allocations, { params }));
}

export async function saveMyIdentity(payload: { githubLogin: string; gitEmails: string[] }) {
  return data<{ identity: WorkHoursIdentity; relinked: number }>(
    await api.put(WORK_HOURS_ENDPOINTS.identity, payload, { timeout: IDENTITY_TIMEOUT_MS }),
  );
}

/** Ventana de la que salieron los repos de una identidad (hoy, 90 días). */
export interface ReposWindow {
  from: string;
  to: string;
  days: number;
}

/**
 * Identidad del equipo con sus repos ya resueltos en el servidor. `repos` en
 * `null` NO es «sin repos»: es «no se pudo calcular», y el motivo viaja en
 * `reposError`.
 */
export interface IdentityRow extends WorkHoursIdentity {
  id: string;
  repos?: string[] | null;
  reposSource?: "buckets" | null;
  reposWindow?: ReposWindow | null;
  reposError?: string | null;
  /** Sin cuenta en la plataforma (alta masiva, `gh_<login>`): se puede pre-vincular (#841). */
  syntheticUser?: boolean;
}

export async function fetchIdentities(): Promise<IdentityRow[]> {
  return data(await api.get(WORK_HOURS_ENDPOINTS.identityList));
}

export async function saveIdentityForUser(
  userId: string,
  payload: { githubLogin?: string; gitEmails?: string[]; teamMemberId?: string | null; userName?: string },
) {
  return data<{ identity: WorkHoursIdentity; relinked: number }>(
    await api.put(WORK_HOURS_ENDPOINTS.identityAdmin, payload, { params: { userId }, timeout: IDENTITY_TIMEOUT_MS }),
  );
}

// ---------------------------------------------------------------------------
// Ligar identidades (#841 / #842)
// ---------------------------------------------------------------------------

/**
 * URL de autorización de GitHub (state firmado por el servidor). Sin la OAuth
 * App configurada el backend responde 503 con `meta.code`
 * `GITHUB_OAUTH_NOT_CONFIGURED`.
 */
export async function fetchGithubConnectUrl(): Promise<{ url: string; scope: string; redirectUri: string | null }> {
  return data(await api.get(WORK_HOURS_ENDPOINTS.githubConnect));
}

/**
 * Cierra el OAuth de GitHub con la sesión actual: el backend canjea el `code` y
 * liga la cuenta SOLO si el `state` es de este mismo usuario (anti login-CSRF).
 */
export async function completeGithubLink(payload: { code: string; state: string }) {
  return data<{
    status: "claimed" | "linked" | "already_linked";
    login: string;
    relinked?: number;
    /** #1185: si el vínculo dejó la cuenta de GitHub como llave para iniciar sesión. */
    loginAccess?: { enabled: boolean; reason: string | null };
  }>(
    await api.post(WORK_HOURS_ENDPOINTS.githubComplete, payload, { timeout: IDENTITY_TIMEOUT_MS }),
  );
}

/** Admin: pre-vincular una identidad por correo (vacío = quitar el pre-vínculo). */
export async function prelinkIdentity(payload: { userId?: string; githubLogin?: string; email: string | null }) {
  return data<{ status: "pending" | "claimed" | "cleared"; email?: string }>(
    await api.put(WORK_HOURS_ENDPOINTS.identityPrelink, payload, { timeout: IDENTITY_TIMEOUT_MS }),
  );
}

/** Admin: contratista externo (`trackHours: false`) o volver a registrar horas. */
export async function setIdentityTracking(payload: { userId: string; trackHours: boolean; reason?: string }) {
  return data<{ status: string }>(await api.put(WORK_HOURS_ENDPOINTS.identityTracking, payload, { timeout: IDENTITY_TIMEOUT_MS }));
}

export async function fetchUnmatched(): Promise<UnmatchedAuthor[]> {
  return data(await api.get(WORK_HOURS_ENDPOINTS.unmatched));
}

export async function fetchClients(): Promise<WorkHoursClientRow[]> {
  return data(await api.get(WORK_HOURS_ENDPOINTS.clientList));
}

/** Configura repos/correos/reporte de un cliente que YA existe en el CRM (sin alta ni baja, #973). */
export async function updateClient(id: string, payload: WorkHoursClientConfig) {
  return data<WorkHoursClientRow>(await api.put(WORK_HOURS_ENDPOINTS.client, payload, { params: { id } }));
}

// ---------------------------------------------------------------------------
// Tiempo, costo, ingreso y ganancia por cliente (#1064). SOLO dueños: el
// backend responde 403 a cualquier otra persona (lleva sueldos e ingresos).
// ---------------------------------------------------------------------------

/** Tipo de cambio usado para pasar el costo a MXN (pesos por unidad). */
export interface EconomicsFx {
  rate: number;
  date: string | null;
  source: string | null;
}

export interface ClientEconomicsRow {
  clientId: string;
  name: string;
  configured: boolean;
  repos: string[];
  /** Horas que cuentan en los repos/proyectos del cliente, en el rango. */
  hours: number;
  /** % de las horas de todo el equipo en el rango. */
  sharePct: number;
  cost: {
    byCurrency: Record<string, number>;
    /** Horas de personas sin sueldo en el padrón (no suman costo). */
    unpricedHours: number;
    /** `null` = falta el tipo de cambio de alguna moneda. */
    totalMxn: number | null;
  };
  /**
   * Lo que el cliente paga POR EL RANGO, en MXN: los cortes de sus negocios con
   * cobro recurrente (facturados o por facturar) y sus demás facturas del rango.
   */
  income: {
    amountMxn: number;
    /** Parte del ingreso del rango que ya entró. */
    collectedMxn?: number;
    /** Facturado y aún con saldo. */
    receivableMxn?: number;
    /** Cortes del rango que todavía no se facturan. */
    toBillMxn?: number;
    /** Negocios ganados sin cobro capturado: no se sabe qué pagan en el rango. */
    unscheduledDeals?: number;
    /** Histórico: suma de los negocios ganados o terminados (referencia). */
    allTimeMxn?: number;
    wonDeals: number;
  };
  /** ingreso − costo; `null` sin tipo de cambio. */
  profitMxn: number | null;
}

export interface ClientEconomics {
  from: string;
  to: string;
  baseCurrency: "MXN";
  teamHours: number;
  attributedHours: number;
  unattributedHours: number;
  unattributedSharePct: number;
  teamCost: ClientEconomicsRow["cost"];
  fx: Record<string, EconomicsFx>;
  fxMissing: string[];
  income: { rule: string; wonStages: string[]; match: string };
  clients: ClientEconomicsRow[];
  totals: { hours: number; costMxn: number | null; incomeMxn: number; profitMxn: number | null };
  rangeClamped?: boolean;
  truncated?: boolean;
}

export async function fetchClientEconomics(params: { from: string; to: string }): Promise<ClientEconomics> {
  return data(await api.get(WORK_HOURS_ENDPOINTS.clientEconomics, { params }));
}

export async function fetchDeliveries(): Promise<DeliveryRow[]> {
  return data(await api.get(WORK_HOURS_ENDPOINTS.deliveryList));
}

export async function fetchRuns(): Promise<RunRow[]> {
  return data(await api.get(WORK_HOURS_ENDPOINTS.runList));
}

/** Manda el reporte diario de verdad. Ver `REPORT_TIMEOUT_MS`. */
export async function runDailyReport(date?: string) {
  return data(await api.post(WORK_HOURS_ENDPOINTS.reportDaily, date ? { date } : {}, { timeout: REPORT_TIMEOUT_MS }));
}

/** Manda el reporte semanal a cada cliente activo. Ver `REPORT_TIMEOUT_MS`. */
export async function runWeeklyReport(weekStart?: string) {
  return data(
    await api.post(WORK_HOURS_ENDPOINTS.reportWeekly, weekStart ? { weekStart } : {}, { timeout: REPORT_TIMEOUT_MS }),
  );
}

// ---------------------------------------------------------------------------
// Entradas de tiempo en minutos (#782 / #783). Estas rutas responden SIN el
// sobre `{ meta, data }`: el cuerpo es el del contrato (`{ data, meta }` o
// `{ ok, id }`).
// ---------------------------------------------------------------------------

export interface TimeEntryRow {
  id: string;
  userId: string;
  projectId: string;
  projectLabel: string;
  date: string;
  minutes: number;
  duration: number;
  note: string;
  kind: TimeEntryKind;
  start: string | null;
  end: string | null;
  evidence: TimeEntryEvidence | null;
  /** #1060: repositorio `owner/nombre` de la entrada; `null` en las anteriores («Sin repositorio»). */
  repo?: string | null;
  /** Clave tal cual se envió (#782); `null` en entradas sin ella. */
  idempotencyKey?: string | null;
  source: TimeEntrySource;
  createdAt: string;
  voided: { at: string; by: string; reason: string } | null;
  /** #843: marcas fuera de asignación (texto del sistema + justificación). Vacío ⇒ normal. */
  flags?: TimeEntryFlag[];
  /** #1281: aprobación del tech lead; `null` o ausente si no la necesitó. Pendiente o rechazada ⇒ no cuenta. */
  approval?: TimeEntryApproval | null;
  /** #1303: ticket al que está ligada la entrada; `null` en entradas anteriores («Sin ticket»). */
  activityId?: string | null;
  activityKey?: string | null;
  activityTitle?: string | null;
  /** #1404: con quiénes fue la reunión; `mirrorOf` ⇒ la registró otra persona por ti. */
  meeting?: TimeEntryMeeting | null;
}

export interface TimeEntriesParams {
  from: string;
  to: string;
  userId?: string;
  all?: boolean;
}

/** Tope de páginas por consulta: 10 × 500 = 5 000 entradas. */
const MAX_TIME_ENTRY_PAGES = 10;

/** Todas las entradas vigentes del rango (recorre las páginas del contrato). */
export async function fetchTimeEntries(params: TimeEntriesParams): Promise<{ rows: TimeEntryRow[]; truncated: boolean }> {
  const rows: TimeEntryRow[] = [];
  let offset = 0;
  for (let page = 0; page < MAX_TIME_ENTRY_PAGES; page += 1) {
    const res = await api.get(WORK_HOURS_ENDPOINTS.timeEntries, { params: { ...params, limit: 500, offset } });
    const body = res?.data as { data?: TimeEntryRow[]; meta?: { hasMore?: boolean; nextOffset?: number | null } } | undefined;
    rows.push(...(body?.data ?? []));
    if (!body?.meta?.hasMore || body.meta.nextOffset === null || body.meta.nextOffset === undefined) return { rows, truncated: false };
    offset = body.meta.nextOffset;
  }
  return { rows, truncated: true };
}

/** Proyecto al que se puede registrar tiempo (`GET workHoursProjects`). */
export interface WorkHoursProjectOption {
  id: string;
  label: string;
  source: string;
  /** Repos `owner/nombre` del proyecto; también comodines `owner/*` (#841). */
  repos: string[];
  /** false ⇒ la persona no está asignada: hace falta justificar (#843). */
  assigned?: boolean;
  /** Repos con actividad reciente de la empresa que caen en el proyecto (#1138). */
  knownRepos?: string[];
}

/** Proyectos: primero los asignados; con `all`, también el resto del catálogo. */
export async function fetchProjects(scope: "assigned" | "all" = "all"): Promise<WorkHoursProjectOption[]> {
  const res = await api.get(WORK_HOURS_ENDPOINTS.projects, { params: { scope } });
  const body = res?.data as { data?: WorkHoursProjectOption[] } | undefined;
  return body?.data ?? [];
}

/** Entrada manual (#1060): reunión, curso, soporte, tiempo facturable… SIEMPRE con repositorio. */
export interface ManualEntryInput {
  projectId: string;
  date: string;
  minutes: number;
  note: string;
  kind: TimeEntryKind;
  repo: string;
  /** #1303: ticket (`work_activities.id`) al que se liga la entrada. Obligatorio en la UI. */
  activityId: string;
  /** #843: por qué registra en un proyecto al que no está asignada. */
  unassignedReason?: string;
  /** #1404: reunión — de qué hora a qué hora (`HH:mm`) y con quiénes. */
  start?: string;
  end?: string;
  meeting?: { attendeeTeamMemberIds: string[]; otherAttendees: string | null };
}

/** #1404: qué pasó con la copia de la reunión de cada asistente. */
export interface MeetingAttendeeResult {
  teamMemberId: string;
  name: string | null;
  ok: boolean;
  id?: string;
  duplicate?: boolean;
  approval?: TimeEntryApprovalStatus | null;
  code?: string;
  message?: string;
}

/** #1404: persona del equipo para elegir asistentes (sin sueldos ni correos). */
export interface WorkHoursPerson {
  teamMemberId: string;
  name: string;
  role: string | null;
  /** `false` ⇒ su ficha no está ligada a Horas: no se le puede registrar la reunión. */
  hasIdentity: boolean;
  isMe: boolean;
}

export async function fetchPeople(): Promise<WorkHoursPerson[]> {
  const res = await api.get(WORK_HOURS_ENDPOINTS.people);
  const body = res?.data as { data?: WorkHoursPerson[] } | undefined;
  return Array.isArray(body?.data) ? body.data : [];
}

/** Slug aleatorio en minúsculas para la clave de idempotencia (`[a-z0-9-]`). */
const randomSlug = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID().toLowerCase()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/** Clave de idempotencia del contrato: `{fecha}-{proyecto}-{slug}`. */
export const manualEntryKey = (date: string, projectId: string, slug = randomSlug()): string => `${date}-${projectId}-ui-${slug}`;

/** Respuesta de `POST workHoursLog`. `approval: "pending"` (#1281) ⇒ quedó esperando al tech lead y no cuenta todavía. */
export interface LogTimeEntryResult {
  ok: boolean;
  id: string;
  duplicate: boolean;
  approval?: TimeEntryApprovalStatus | null;
  /** #1404: resultado por asistente cuando la entrada es una reunión. */
  attendees?: MeetingAttendeeResult[];
}

/** Registra una entrada manual (`POST workHoursLog`, source `ui`). */
export async function logTimeEntry(input: ManualEntryInput): Promise<LogTimeEntryResult> {
  const res = await api.post(WORK_HOURS_ENDPOINTS.log, {
    ...input,
    unassignedReason: input.unassignedReason || undefined,
    idempotencyKey: manualEntryKey(input.date, input.projectId),
  });
  return res?.data as LogTimeEntryResult;
}

// ---------------------------------------------------------------------------
// Aprobación del tech lead (#1281). Mismo contrato sin sobre que las entradas.
// ---------------------------------------------------------------------------

/** ¿Quien usa la consola aprueba horas? Y cuántas esperan (dentro de su alcance). */
export interface ApprovalAccess {
  canApprove: boolean;
  pending: number;
  pendingHours: number;
}

/** Fila de «Horas por aprobar». `rate`/`amount` SOLO llegan a los dueños. */
export interface ApprovalRow {
  id: string;
  userId: string;
  userName: string;
  teamMemberId: string | null;
  projectId: string;
  projectLabel: string;
  repo: string | null;
  date: string;
  minutes: number;
  hours: number;
  note: string;
  kind: TimeEntryKind;
  start: string | null;
  end: string | null;
  evidence: TimeEntryEvidence | null;
  source: TimeEntrySource;
  createdAt: string;
  flags: TimeEntryFlag[];
  approval: TimeEntryApproval;
  /** ¿Quien consulta puede aprobar o rechazar ESTA fila? (Nunca la propia.) */
  canDecide: boolean;
  rate?: { hourlyCost: number; currency: string; basis: string } | null;
  /**
   * `value` va en la moneda de pago de la persona; `mxn` y `usd` son su
   * equivalente con el tipo de cambio de la plataforma (`null` si no lo hay).
   */
  amount?: { value: number; currency: string; mxn?: number | null; usd?: number | null } | null;
  /** #1303: ticket ligado (clave y título denormalizados). */
  activityId?: string | null;
  activityKey?: string | null;
  activityTitle?: string | null;
}

export interface ApprovalsResponse {
  data: ApprovalRow[];
  meta: {
    status: TimeEntryApprovalStatus;
    total: number;
    totalMinutes: number;
    totalHours: number;
    canSeeAmounts: boolean;
    scope: "all" | "squads";
    /** Sólo dueños: importe total por moneda (nunca sumado entre monedas). */
    amountByCurrency?: Record<string, number>;
    unpriced?: number;
    /** Sólo dueños: total equivalente en pesos y en dólares (`null` sin tipo de cambio). */
    amountTotals?: { mxn: number | null; usd: number | null };
    /** Sólo dueños: tipo de cambio usado (pesos por unidad) y monedas sin él. */
    fx?: Record<string, { rate: number; date: string | null; source: string | null }>;
    fxMissing?: string[];
  };
}

/** `GET workHoursApprovalAccess`. Un 403 o un error de red ⇒ no aprueba. */
export async function fetchApprovalAccess(): Promise<ApprovalAccess> {
  try {
    const res = await api.get(WORK_HOURS_ENDPOINTS.approvalAccess);
    const body = res?.data as Partial<ApprovalAccess> | undefined;
    return { canApprove: body?.canApprove === true, pending: Number(body?.pending) || 0, pendingHours: Number(body?.pendingHours) || 0 };
  } catch {
    return { canApprove: false, pending: 0, pendingHours: 0 };
  }
}

/** `GET workHoursApprovals`: pendientes (sin rango) o decididas (del rango). */
export async function fetchApprovals(params: { status?: TimeEntryApprovalStatus; from?: string; to?: string } = {}): Promise<ApprovalsResponse> {
  const res = await api.get(WORK_HOURS_ENDPOINTS.approvals, { params });
  const body = res?.data as Partial<ApprovalsResponse> | undefined;
  return {
    data: body?.data ?? [],
    meta: { status: params.status ?? "pending", total: 0, totalMinutes: 0, totalHours: 0, canSeeAmounts: false, scope: "squads", ...(body?.meta ?? {}) },
  };
}

/** `POST workHoursEntryApprove`. */
export async function approveTimeEntry(id: string): Promise<{ ok: boolean; id: string; approval: TimeEntryApproval }> {
  const res = await api.post(WORK_HOURS_ENDPOINTS.entryApprove, { id });
  return res?.data as { ok: boolean; id: string; approval: TimeEntryApproval };
}

/** `POST workHoursEntryReject` — el motivo es obligatorio. */
export async function rejectTimeEntry(id: string, reason: string): Promise<{ ok: boolean; id: string; approval: TimeEntryApproval }> {
  const res = await api.post(WORK_HOURS_ENDPOINTS.entryReject, { id, reason });
  return res?.data as { ok: boolean; id: string; approval: TimeEntryApproval };
}

/** Mensaje del servidor de una entrada rechazada (`{ ok:false, error:{ code, message } }`), o null. */
export function timeEntryErrorMessage(err: unknown): string | null {
  const data = (err as { response?: { data?: { error?: { message?: unknown } } } })?.response?.data;
  const message = data?.error?.message;
  return typeof message === "string" && message.trim() ? message : null;
}

/** Código del contrato (`ACTIVITY_REQUIRED`, `KIND_NOT_ALLOWED`…) de una entrada rechazada, o null. */
export function timeEntryErrorCode(err: unknown): string | null {
  const data = (err as { response?: { data?: { error?: { code?: unknown } } } })?.response?.data;
  const code = data?.error?.code;
  return typeof code === "string" && code.trim() ? code : null;
}

// ---------------------------------------------------------------------------
// Actividades (épica #1299): catálogo por puesto y tickets. Mismo contrato sin
// sobre que las entradas. El «Tipo» del formulario manual sale del catálogo
// (#1306) y toda entrada manual queda ligada a un ticket (#1303).
// ---------------------------------------------------------------------------

/** Respuesta de `GET workHoursActivityCatalog`: el catálogo efectivo y lo que me toca. */
export interface ActivityCatalogResponse {
  data: ActivityType[];
  meta: {
    canManage: boolean;
    /** Puesto de quien consulta (`team_members.role`), o null si no está ligado a un colaborador. */
    role: string | null;
    /** Claves que mi puesto puede realizar. */
    allowedKeys: string[];
    /** Claves que mi puesto puede registrar A MANO (sin `dev`, que nace de commits). */
    manualKeys: string[];
    materialized?: boolean;
  };
}

export async function fetchActivityCatalog(): Promise<ActivityCatalogResponse> {
  const res = await api.get(WORK_HOURS_ENDPOINTS.activityCatalog);
  const body = res?.data as Partial<ActivityCatalogResponse> | undefined;
  const meta = body?.meta ?? ({} as Partial<ActivityCatalogResponse["meta"]>);
  return {
    data: body?.data ?? [],
    meta: {
      canManage: meta.canManage === true,
      role: typeof meta.role === "string" ? meta.role : null,
      allowedKeys: Array.isArray(meta.allowedKeys) ? meta.allowedKeys : [],
      manualKeys: Array.isArray(meta.manualKeys) ? meta.manualKeys : [],
      materialized: meta.materialized,
    },
  };
}

export interface ActivitySearchParams {
  q?: string;
  sprintId?: string;
  backlog?: boolean;
  includeDone?: boolean;
  limit?: number;
}

/** Fila de `GET workHoursActivities`; `cost` solo llega a los dueños. */
export type ActivityRow = WorkActivity & { cost?: { estimated: number | null; actual: number; currency: string } | null };

export interface ActivitiesResponse {
  data: ActivityRow[];
  meta: { total: number; hasMore?: boolean; canManage?: boolean; isOwner?: boolean; activeSprintId?: string | null };
}

/** Busca tickets (sprint activo primero cuando no se filtra por sprint). */
export async function searchActivities(params: ActivitySearchParams = {}): Promise<ActivitiesResponse> {
  const query: Record<string, string | number> = {};
  if (params.q) query.q = params.q;
  if (params.sprintId) query.sprintId = params.sprintId;
  if (params.backlog) query.backlog = 1;
  if (params.includeDone) query.includeDone = 1;
  query.limit = params.limit ?? 50;
  const res = await api.get(WORK_HOURS_ENDPOINTS.activities, { params: query });
  const body = res?.data as Partial<ActivitiesResponse> | undefined;
  return { data: body?.data ?? [], meta: { total: 0, ...(body?.meta ?? {}) } };
}

/** Lo mínimo para crear un ticket desde «Registrar tiempo»: título y tipo del catálogo. */
export interface CreateActivityInput {
  title: string;
  typeKey: string;
  description?: string | null;
  projectId?: string | null;
  repo?: string | null;
  sprintId?: string | null;
  /** Clave idempotente opcional (`manual:<proyecto>:<slug>`). */
  key?: string | null;
}

export interface CreateActivityResult {
  ok: boolean;
  /** false ⇒ ya existía (misma `key`/`githubRef`) y se devolvió el existente. */
  created: boolean;
  data: WorkActivity;
}

/** `POST workHoursActivities`: idempotente por `key`. */
export async function createActivity(input: CreateActivityInput): Promise<CreateActivityResult> {
  const res = await api.post(WORK_HOURS_ENDPOINTS.activities, input);
  return res?.data as CreateActivityResult;
}

/** Anula una entrada (solo administradores). Nunca borra. */
export async function voidTimeEntry(id: string, reason: string): Promise<{ ok: boolean; id: string }> {
  const res = await api.post(WORK_HOURS_ENDPOINTS.entryVoid, { id, reason });
  return res?.data as { ok: boolean; id: string };
}
